import Page from 'component/Page';
import { useEffect, useMemo, useState } from 'react';
import sendRequest, { sendPost } from 'util/request';
import string from 'util/string';
import Urls from 'util/urls';
import Table from 'component/Table';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'redux/user/user.selector';
import { ACCOUNT_ROLE_ADMIN } from 'util/permission';
import { useCallback } from 'react';
import { getParams } from 'util/utils';
import {
	nNU,
	showConfirm,
	showToast,
	formatMinutes,
	getRemainingTime as getAssigningTime,
} from 'util/utils';


const headers = [
	string.APP_SC_ASSIGN_ID_USERNAME,
	string.APP_SC_ASSIGN_ID_DISPLAY_NAME,
	string.APP_SC_ASSIGN_ID_POC_ID,
	string.APP_SC_ASSIGN_ID_TYPE,
	string.APP_SC_ASSIGN_ID_REMAIN_TIME,
	string.APP_SC_ASSIGN_ID_ASSIGNING,
	string.APP_SC_ASSIGN_ID_REMAIN_TIME_AFTER,
	string.APP_SC_ASSIGN_ID_VOL,
];

const SERVICE_CODE_VOLUME = 50 * 3600;

const ServiceCodeAssignAppId = ({ data = { id: null } }) => {
	//-------------------------------HOOKS-------------------------------------------------------------
	const params = getParams();
	const serviceCodeId = data.id || params?.id;

	const currentUser = useSelector(selectCurrentUser);
	const isAdmin = useMemo(
		() => currentUser.accountRole === ACCOUNT_ROLE_ADMIN,
		[ currentUser.accountRole ]
	);
	const isMonitorOnly = useMemo(
		() => currentUser.isMonitorOnly === 1,
		[ currentUser.isMonitorOnly ]
	);

	const [ page, setPage ] = useState(1);
	const [ total, setTotal ] = useState(0);
	const [ pageSize, setPageSize ] = useState(100);
	// const [ pageSize, setPageSize ] = useState(10);

	const [ searchId, setSearchId ] = useState('');
	const [ debouncedSearchId, setDebouncedSearchId ] = useState(searchId);

	const [ users, setUsers ] = useState([]);
	const [ assignUsers, setAssignUsers ] = useState([]);
	const [ assignTotal, setAssignTotal ] = useState(0);

	const [ assign, setAssign ] = useState(null);
	const [ serviceCode, setServiceCode ] = useState(null);
	const [ serviceCodeConfig, setServiceCodeConfig ] = useState(null);
	const [ assignableVolume, setAssignableVolume ] = useState(0);
	const [ refreshPage, setRefreshPage ] = useState(0);
	const [ refresh, setRefresh ] = useState(false);
	const [ volumeOver, setVolumeOver ] = useState(false);

	//------------------------------FUNCTIONS----------------------------------------------------------
	useEffect(() => {
		if (!nNU(serviceCodeId)) {
			return;
		}
		sendRequest({
			url: `${Urls.servicecode.info}/${serviceCodeId}`,
			method: 'GET',
		}).then((response) => {
			const { result } = response;
			setServiceCode(result);
			let volume = result?.volume - result?.volumeUsage;
			setAssignableVolume(volume);
		}).catch((e) => {

		});
	}, [ serviceCodeId ]);

	// useEffect(() => {
	// 	if (!nNU(serviceCodeId)) {
	// 		return;
	// 	}
	// 	sendRequest({
	// 		url: `${Urls.servicecode.config}/${serviceCodeId}`,
	// 		method: 'GET',
	// 	}).then((response) => {
	// 		const { result } = response;
	// 		setServiceCodeConfig(result);
	// 	}).catch((e) => {

	// 	});
	// }, [serviceCodeId]);

	const doSearch = useCallback(
		async (page, pageSize, searchKeyword, getAll = false) => {
			if (!nNU(currentUser.id)) {
				return;
			}
			let data = await sendPost({
				url: Urls.pocaccount.userlist,
				body: {
					id: currentUser.id,
					offset: (page - 1) * pageSize,
					size: pageSize,
					keyword: searchKeyword,
				},
			});
			const { total, result } = data;
			if (!getAll) {
				setUsers(result);
				setTotal(total);
			}
			let remainingTimePending = 0;
			for(let i = 0; i < result.length; i++) {
				if(nNU(result[i]?.serviceCodePending)) {
					remainingTimePending = remainingTimePending + result[i].serviceCodePending;
				}
			}
			if(remainingTimePending > 0){
				const tictoc = new Date().getSeconds() + 1;
				setRefreshPage(tictoc);
				// console.log(tictoc.toString());
			} else {
				setRefreshPage(0);
			}
			// console.log(remainingTimePending);
			return result;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ currentUser ]
	);
	useEffect(() => {
		const timerId = setTimeout(() => {
			setDebouncedSearchId(searchId);
		}, 500);

		return () => {
			clearTimeout(timerId);
		};
	}, [ searchId ]);
	useEffect(() => {
		doSearch(page, pageSize, debouncedSearchId);
	}, [ page, pageSize, debouncedSearchId, doSearch ]);

	const dataTable = useCallback(
		(data) => data.map((raw, key) => ({
			key: raw.id,
			data: [
				raw?.username,
				raw?.displayName,
				raw?.pocId.toString().padStart(6, '0'),
				(raw?.status === 0) ? string.APP_SC_ASSIGN_ID_TYPE_DISABLE :
					(raw?.planType === 0) ?
						string.APP_SC_ASSIGN_ID_TYPE_MONTHLY_PAID : string.APP_SC_ASSIGN_ID_TYPE_PREPAID,
				(raw?.status === 0) || (raw?.planType === 1) ? formatMinutes(raw?.remainingTime > 60 ? raw?.remainingTime - 60 : 0) : "-",
				((raw?.status === 0) || (raw?.planType === 1)) && nNU(raw?.pendingVolume) && (raw?.pendingVolume > 0) ? formatMinutes(getAssigningTime(raw?.pendingVolume)) : "-",
				(raw?.status === 0) || (raw?.planType === 1) ? formatMinutes(getRemainingTime(raw) + getAssigningTime(raw?.pendingVolume)) : "-",
				(raw?.status === 0) || (raw?.planType === 1) ? getAssignVol(raw) : "-",
			],
			raw,
		})),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ assignTotal, assignUsers ]
	);
	const getAssignVol = (userData) => {
		if (userData?.pocId === null) {
			return 0;
		} else if ((userData?.status === 1) && (userData?.planType === 0)) {
			return 0;
		}
		if (assignUsers.length === 0) {
			return 0;
		}
		let data = assignUsers;
		let assignVol = 0;
		for (let i = 0; i < data.length; i++) {
			if (data[ i ].pocId === userData?.pocId) {
				assignVol = data[ i ].volume;
				break;
			}
		}
		return assignVol;
	};
	const getRemainingTime = (userData) => {
		if ((userData?.status === 1) && (userData?.planType === 0)) {
			return 0;
		}
		let data = assignUsers;
		if (data.length === 0) {
			if (userData?.remainingTime > 60) {
				return userData?.remainingTime - 60;
			} else {
				return 0;
			}
		} else {
			let checkExist = 0;
			let pos = 0;
			for (let i = 0; i < data.length; i++) {
				if (data[ i ].pocId === userData?.pocId) {
					pos = i;
					checkExist = 1;
					break;
				}
			}
			if (checkExist === 1) {
				const interval = SERVICE_CODE_VOLUME;
				let assignTime = data[ pos ].volume * interval;
				if (userData?.remainingTime > 60) {
					return assignTime + userData?.remainingTime - 60;
				} else {
					return assignTime + userData?.remainingTime;
				}
			} else {
				if (userData?.remainingTime > 60) {
					return userData?.remainingTime - 60;
				} else {
					return 0;
				}
			}
		}
	};

	const incAssignVol = (userData) => {
		if ((assignableVolume - assignTotal) < 1) {
			return;
		}
		if ((userData?.status === 1) && (userData?.planType === 0)) {
			return;
		} else if ((userData?.remainingTime + SERVICE_CODE_VOLUME * userData?.pendingVolume) > 7900 * 3600) {
			setVolumeOver(true);
			return;
		}
		let data = assignUsers;
		let update = 0;
		if (data.length === 0) {
			data = [ { pocId: userData?.pocId, volume: 1 } ];
			update = 1;
		} else {
			let checkExist = 0;
			let pos = 0;
			for (let i = 0; i < data.length; i++) {
				if (data[ i ].pocId === userData?.pocId) {
					//data[i].volume = data[i].volume + 1;
					pos = i;
					checkExist = 1;
					break;
				}
			}
			if (checkExist === 0) {
				data = [ ...data, { pocId: userData?.pocId, volume: 1 } ];
				update = 1;
			} else {
				let volume = data[ pos ].volume;
				const interval = SERVICE_CODE_VOLUME;
				let remainingTime = volume * interval + userData?.remainingTime + SERVICE_CODE_VOLUME * userData?.pendingVolume;
				if (remainingTime < 7950 * 3600) {
					data[ pos ].volume = data[ pos ].volume + 1;
					update = 1;
				} else {
					// showToast(string.APP_CHANNEL_NAME_EMPTY_ERR);
					setVolumeOver(true);
					return;
				}
			}
		}

		if (update > 0) {
			let total = 0;
			for (let i = 0; i < data.length; i++) {
				total = total + data[ i ].volume;
			}
			setAssignUsers(data);
			setAssignTotal(total);
		}
	};
	const decAssignVol = (userData) => {
		if ((userData?.status === 1) && (userData?.planType === 0)) {
			return;
		}
		let data = assignUsers;
		if (data.length === 0) {
			return;
		} else {
			let checkExist = 0;
			let decPos = 0;
			for (let i = 0; i < data.length; i++) {
				if (data[ i ].pocId === userData?.pocId) {
					data[ i ].volume = data[ i ].volume - 1;
					checkExist = 1;
					decPos = i;
					break;
				}
			}
			if (checkExist === 1) {
				let data2 = [];
				if (data[ decPos ].volume === 0) {
					for (let i = 0; i < data.length; i++) {
						if (i !== decPos) {
							data2 = [ ...data2, data[ i ] ];
						}
					}
				} else {
					data2 = data;
				}
				let total = 0;
				if (data2.length > 0) {
					for (let i = 0; i < data2.length; i++) {
						total = total + data2[ i ].volume;
					}
				}
				setAssignUsers(data2);
				setAssignTotal(total);
			}
		}
	};

	const mainActions = (assignableVolume > 0) ? [
		{
			icon: 'assign', title: string.APP_SC_ASSIGN_ID, onClick: () => {
				if (assignUsers.length > 0) {
					setAssign(assignUsers);
				}
			}
		},
	] : [];
	const actions = (assignableVolume > 0) ? [
		({ raw }) => {
			if (((raw?.status === 0) || (raw?.planType === 1)) && (raw?.serviceCodePending === 0)){
				return { render: 'volumeminus', onClick: ({ raw }) => { decAssignVol(raw) }, className: 'custom-volume-add-icon' };
			}
		},
		({ raw }) => {
			if (((raw?.status === 0) || (raw?.planType === 1)) && (raw?.serviceCodePending === 0)) {
				return { render: 'volumeadd', onClick: ({ raw }) => { incAssignVol(raw) }, className: 'custom-volume-add-icon' };
			}
		},
	] : [];
	useEffect(() => {
		if (assign === null) {
			return;
		}
		let data = assign;
		let codeId = Number(serviceCodeId);
		if (data.length > 0) {
			let bodyData = {
				codeId: codeId,
				data: data
			};
			showConfirm({
				body: string.APP_SC_ASSIGN_ID_CONFIRM,
				defaultAction: () => {
					sendPost({
						url: Urls.servicecode.assign,
						body: bodyData,
					}).then((res) => {
						showToast(string.APP_SC_ASSIGN_ID_SUCCESS, 'success');
						setAssign(null);
						setAssignUsers([]);
						let volume = assignableVolume - assignTotal;
						setAssignableVolume(volume);
						setAssignTotal(0);
						doSearch(page, pageSize);
					}).catch((e) => {
						setAssign(null);
						setAssignUsers([]);
						setAssignTotal(0);
					});
				},
			});
		}
		// setAssign(null);
		// setAssignUsers([]);
		// setAssignTotal(0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ assign ]);
	useEffect(() =>{
		if(refreshPage === 0){
			return;
		}
		if(assignTotal > 0){
			return;
		}

		const timerId = setTimeout(() => {
			setRefresh(true);
		}, 10000);

		return () => {
			clearTimeout(timerId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	},[ refreshPage ]);

	useEffect(() => {
		if(refresh === false){
			return;
		}
		doSearch(page, pageSize);
		setRefresh(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	},[ refresh ]);

	useEffect(() =>{
		if(volumeOver === false){
			return;
		}
		showToast(string.APP_SC_ASSIGN_VOLUME_OVER,'warning');
		setVolumeOver(false);

	},[volumeOver]);

	//-------------------------------RETURN------------------------------------------------------------
	return (
		<>
			<Page
				title={`合計：${assignableVolume}、割当：${assignTotal}／残り：${assignableVolume - assignTotal}`}
				body={
					<>
						{/* <FormControls controls={formControls} /> */}
						<Table
							className="nowrap"
							page={page}
							pageSize={pageSize}
							total={total}
							onPageChanged={setPage}
							onPageSizeChanged={setPageSize}
							headers={headers}
							data={dataTable(users)}
							// getCSVData={async () => {
							// 		return dataTable(await doSearch(1, total, null, true));
							// }}
							// csvFileName="AppUserList"
							isPaging={true}
							mainActions={mainActions}
							actions={actions}
							search={false}
						// onSearch={(value) => setSearchId(value)}
						// placeHolder={`${string.POC_LOGIN_ID}、${string.POC_DISPLAY_NAME}`}
						/>
					</>
				}
			/>
		</>
	);
};

export default ServiceCodeAssignAppId;
